// Tank 2D Battle Arena 30Hz Server Engine (2-8 Players)
export class TankEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.width = 900;
    this.height = 600;
    this.tanks = {};
    this.bullets = [];
    this.obstacles = [];
    this.loopInterval = null;
  }

  generateMap() {
    this.obstacles = [
      { x: 180, y: 120, w: 100, h: 20, type: 'wall' },
      { x: 620, y: 120, w: 100, h: 20, type: 'wall' },
      { x: 180, y: 460, w: 100, h: 20, type: 'wall' },
      { x: 620, y: 460, w: 100, h: 20, type: 'wall' },
      { x: 400, y: 220, w: 100, h: 160, type: 'wall' },
      { x: 280, y: 280, w: 40, h: 40, type: 'crate', health: 2 },
      { x: 580, y: 280, w: 40, h: 40, type: 'crate', health: 2 }
    ];
  }

  start() {
    this.generateMap();
    this.tanks = {};
    this.bullets = [];

    const colors = ['#22c55e', '#38bdf8', '#ef4444', '#eab308', '#a855f7', '#ec4899', '#f97316', '#06b6d4'];
    const spawns = [
      { x: 80, y: 80, a: 0 },
      { x: 820, y: 520, a: Math.PI },
      { x: 820, y: 80, a: Math.PI },
      { x: 80, y: 520, a: 0 },
      { x: 450, y: 80, a: Math.PI / 2 },
      { x: 450, y: 520, a: -Math.PI / 2 },
      { x: 80, y: 300, a: 0 },
      { x: 820, y: 300, a: Math.PI }
    ];

    const activePlayers = this.room.players.map(p => ({
      ...p,
      isBot: !!p.isBot || p.id.startsWith('bot_')
    }));

    if (activePlayers.length < 2) {
      activePlayers.push(
        { id: 'bot_tank1', name: '🤖 TankBot Alpha', isBot: true },
        { id: 'bot_tank2', name: '🤖 PanzerBot', isBot: true }
      );
    }

    activePlayers.forEach((p, idx) => {
      const sp = spawns[idx % spawns.length];
      this.tanks[p.id] = {
        id: p.id,
        name: p.name,
        color: colors[idx % colors.length],
        x: sp.x,
        y: sp.y,
        angle: sp.a,
        turretAngle: sp.a,
        health: 3,
        isAlive: true,
        isBot: p.isBot,
        input: { forward: false, backward: false, left: false, right: false },
        lastShotTime: 0
      };
    });

    if (this.loopInterval) clearInterval(this.loopInterval);
    this.loopInterval = setInterval(() => this.update(), 1000 / 30);
  }

  handleInput(playerId, input) {
    const t = this.tanks[playerId];
    if (t && t.isAlive) {
      t.input = input;
    }
  }

  handleTurret(playerId, angle) {
    const t = this.tanks[playerId];
    if (t && t.isAlive) {
      t.turretAngle = angle;
    }
  }

  fireBullet(playerId) {
    const t = this.tanks[playerId];
    const now = Date.now();
    if (!t || !t.isAlive || now - t.lastShotTime < 450) return;

    t.lastShotTime = now;
    const speed = 9.0;
    this.bullets.push({
      id: `b_${now}_${Math.random()}`,
      ownerId: playerId,
      x: t.x + Math.cos(t.turretAngle) * 22,
      y: t.y + Math.sin(t.turretAngle) * 22,
      vx: Math.cos(t.turretAngle) * speed,
      vy: Math.sin(t.turretAngle) * speed,
      bounces: 2,
      life: 90
    });
  }

  update() {
    let aliveCount = 0;
    let lastAlive = null;

    // Update Tanks
    for (const id in this.tanks) {
      const t = this.tanks[id];
      if (!t.isAlive) continue;

      aliveCount++;
      lastAlive = t;

      // AI Bot Navigation & Combat
      if (t.isBot) {
        let nearest = null;
        let minDist = Infinity;
        for (const otherId in this.tanks) {
          if (otherId !== id && this.tanks[otherId].isAlive) {
            const d = Math.hypot(this.tanks[otherId].x - t.x, this.tanks[otherId].y - t.y);
            if (d < minDist) {
              minDist = d;
              nearest = this.tanks[otherId];
            }
          }
        }

        if (nearest) {
          const targetAngle = Math.atan2(nearest.y - t.y, nearest.x - t.x);
          t.turretAngle = targetAngle;

          let diff = targetAngle - t.angle;
          while (diff < -Math.PI) diff += Math.PI * 2;
          while (diff > Math.PI) diff -= Math.PI * 2;

          if (diff > 0.1) t.angle += 0.06;
          else if (diff < -0.1) t.angle -= 0.06;

          t.input.forward = minDist > 100;

          if (Date.now() - t.lastShotTime > 1800 + Math.random() * 800) {
            this.fireBullet(t.id);
          }
        }
      }

      if (t.input.left) t.angle -= 0.08;
      if (t.input.right) t.angle += 0.08;

      let moveSpeed = 0;
      if (t.input.forward) moveSpeed = 3.5;
      if (t.input.backward) moveSpeed = -2.2;

      if (moveSpeed !== 0) {
        const nextX = t.x + Math.cos(t.angle) * moveSpeed;
        const nextY = t.y + Math.sin(t.angle) * moveSpeed;

        if (nextX > 20 && nextX < this.width - 20 && nextY > 20 && nextY < this.height - 20) {
          // Check collision with obstacles
          const hitObstacle = this.obstacles.some((o) => (
            nextX + 16 > o.x && nextX - 16 < o.x + o.w &&
            nextY + 16 > o.y && nextY - 16 < o.y + o.h
          ));
          if (!hitObstacle) {
            t.x = nextX;
            t.y = nextY;
          }
        }
      }
    }

    // Update Bullets
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.x += b.vx;
      b.y += b.vy;
      b.life--;

      // Boundary Bounce
      if (b.x < 10 || b.x > this.width - 10) { b.vx *= -1; b.bounces--; }
      if (b.y < 10 || b.y > this.height - 10) { b.vy *= -1; b.bounces--; }

      // Obstacle Bounce
      for (const o of this.obstacles) {
        if (b.x > o.x && b.x < o.x + o.w && b.y > o.y && b.y < o.y + o.h) {
          b.vx *= -1;
          b.vy *= -1;
          b.bounces--;
          if (o.type === 'crate') {
            o.health--;
            if (o.health <= 0) {
              const idx = this.obstacles.indexOf(o);
              if (idx !== -1) this.obstacles.splice(idx, 1);
            }
          }
          break;
        }
      }

      // Tank Hit
      for (const id in this.tanks) {
        const t = this.tanks[id];
        if (t.isAlive && Math.hypot(b.x - t.x, b.y - t.y) < 18) {
          t.health--;
          b.life = 0;
          if (t.health <= 0) {
            t.isAlive = false;
          }
          break;
        }
      }

      if (b.life <= 0 || b.bounces < 0) {
        this.bullets.splice(i, 1);
      }
    }

    this.io.to(this.room.code).emit('tank_state', {
      tanks: this.tanks,
      bullets: this.bullets,
      obstacles: this.obstacles
    });

    if (aliveCount <= 1 && Object.keys(this.tanks).length > 1) {
      clearInterval(this.loopInterval);
      setTimeout(() => {
        this.io.to(this.room.code).emit('tank_gameOver', { winner: lastAlive });
      }, 1500);
    }
  }

  destroy() {
    if (this.loopInterval) clearInterval(this.loopInterval);
  }
}
